import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import Login from "./shared/components/Login";
import ProtectedRoute from "./shared/components/ProtectedRoute";
import {
  applyAuthLoginPayload,
  checkSession,
  clearLegacyAuthStorage,
  getAuthItem,
  getAuthUser,
  subscribeToAuthSession,
  syncSessionTimeouts,
} from "./utils/auth";
import { API_BASE_URL } from "./config/apiConfig";
import { refreshCurrentAuthUser } from "./services/authService";
import { resolveModuleAccessRedirectPath } from "./utils/moduleAccess";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TwoFactor = lazy(() => import("./shared/components/TwoFactor"));
const Layout = lazy(() => import("./layouts/Layout"));
const PageNotFound = lazy(() => import("./shared/components/PageNotFound"));
const ITContactMatrix = lazy(
  () => import("./shared/components/ITContactMatrix"),
);
const CreateEmployee = lazy(
  () => import("./features/employees/pages/CreateEmployee"),
);
const ActiveEmployees = lazy(
  () => import("./features/employees/pages/ActiveEmployees"),
);
const RelievedEmployees = lazy(
  () => import("./features/employees/pages/RelievedEmployees"),
);
const EditEmployee = lazy(
  () => import("./features/employees/pages/EditEmployee"),
);
const ModuleAccessPage = lazy(
  () => import("./features/employees/pages/ModuleAccessPage"),
);
const ModuleAccessLogsPage = lazy(
  () => import("./features/employees/pages/ModuleAccessLogsPage"),
);
const NewTicket = lazy(() => import("./features/tickets/pages/NewTicket"));
const OpenedTickets = lazy(
  () => import("./features/tickets/pages/OpenedTicket"),
);
const TicketHistoryPage = lazy(
  () => import("./features/tickets/pages/TicketHistory"),
);
const ClosedTickets = lazy(
  () => import("./features/tickets/pages/ClosedTicket"),
);
const TicketSystemDashboard = lazy(
  () => import("./features/ticket_system/pages/TicketSystemDashboard"),
);
const TicketSystemDetails = lazy(
  () => import("./features/ticket_system/pages/TicketSystemDetails"),
);
const OfferLetterRequest = lazy(
  () => import("./features/hrd/pages/OfferLetterRequest"),
);
const OfferLetterDashboard = lazy(
  () => import("./features/hrd/pages/OfferLetterDashboard"),
);
const OfferLetterEdit = lazy(
  () => import("./features/hrd/pages/OfferLetterEdit"),
);
const CreatePayslip = lazy(() => import("./features/hrd/pages/CreatePayslip"));
const EditPayslip = lazy(() => import("./features/hrd/pages/EditPayslip"));
const PayslipDashboard = lazy(
  () => import("./features/hrd/pages/PayslipDashboard"),
);
const SignatureCenter = lazy(
  () => import("./features/hrd/pages/SignatureCenter"),
);
const RelievingRequest = lazy(
  () => import("./features/hrd/pages/RelievingRequest"),
);
const EditRelieving = lazy(() => import("./features/hrd/pages/EditRelieving"));
const RelievedDashboard = lazy(
  () => import("./features/hrd/pages/RelievedDashboard"),
);
const ManpowerRequest = lazy(
  () => import("./features/hrd/pages/ManpowerDashboard"),
);
const NewRequest = lazy(
  () => import("./features/hrd/pages/ManpowerNewRequest"),
);
const RequestDetail = lazy(
  () => import("./features/hrd/pages/ManpowerRequestEdit"),
);
const UniformRequest = lazy(
  () => import("./features/hrd/pages/UniformRequest"),
);
const UniformRequestForm = lazy(
  () => import("./features/hrd/pages/UniformRequestForm"),
);
const InventoryDashboard = lazy(
  () => import("./features/inventory/pages/InventoryDashboard"),
);
const InventoryNewRequest = lazy(
  () => import("./features/inventory/pages/InventoryNewRequest"),
);
const InventoryRequestDetail = lazy(
  () => import("./features/inventory/pages/InventoryRequestDetail"),
);
const StockMaintainancePage = lazy(
  () => import("./features/inventory/pages/StockMaintainancePage"),
);
const InventoryMasterPage = lazy(
  () => import("./features/inventory/pages/InventoryMasterPage"),
);
const VendorRegisterDashboard = lazy(
  () => import("./features/inventory/pages/VendorRegisterDashboard"),
);
const VendorRegisterFormPage = lazy(
  () => import("./features/inventory/pages/VendorRegisterFormPage"),
);
const LeadMasterPage = lazy(
  () => import("./features/new_connections/LeadMasterPage"),
);
const LeadFormPage = lazy(
  () => import("./features/new_connections/LeadFormPage"),
);
const LcoDashboard = lazy(() => import("./features/lco/pages/LcoDashboard"));
const CreateLco = lazy(() => import("./features/lco/pages/CreateLco"));
const EditLco = lazy(() => import("./features/lco/pages/EditLco"));
const ViewLco = lazy(() => import("./features/lco/pages/ViewLco"));
const CustomerDelightDashboard = lazy(
  () => import("./features/customer_delight/pages/CustomerDelightDashboard"),
);
const CustomerDelightAnalyticsDashboard = lazy(
  () =>
    import("./features/customer_delight/components/CustomerDelightDashboard"),
);
const CreateCustomerDelight = lazy(
  () => import("./features/customer_delight/pages/CreateCustomerDelight"),
);
const EditCustomerDelight = lazy(
  () => import("./features/customer_delight/pages/EditCustomerDelight"),
);
const ViewCustomerDelight = lazy(
  () => import("./features/customer_delight/pages/ViewCustomerDelight"),
);
const CustomerDetailsDashboard = lazy(
  () => import("./features/customer_details/CustomerDetailsDashboard"),
);
const CreateCustomerDetails = lazy(
  () => import("./features/customer_details/pages/CreateCustomerDetails"),
);
const EditCustomerDetails = lazy(
  () => import("./features/customer_details/pages/EditCustomerDetails"),
);
const ViewCustomerDetails = lazy(
  () => import("./features/customer_details/pages/ViewCustomerDetails"),
);
const ChatPage = lazy(() => import("./features/chats/pages/chat"));
const MeetingPage = lazy(() => import("./features/meeting/pages/Meeting"));
const NocDashboardPage = lazy(() => import("./features/noc/DashboardPage"));
const NocFormPage = lazy(() => import("./features/noc/FormPage"));
const NocDetailPage = lazy(() => import("./features/noc/DetailPage"));
const NldMasterPage = lazy(
  () => import("./features/noc/Nld master/NldMasterPage"),
);
const NldMasterAdd = lazy(() => import("./features/noc/Nld master/AddNld"));
const NldMasterDetail = lazy(
  () => import("./features/noc/Nld master/NldMasterDetail"),
);
const TowerStatus = lazy(
  () => import("./features/noc/towerStatus/TowerStatus"),
);
const PreplanDashboard = lazy(
  () => import("./features/preplan/pages/PreplanDashboard"),
);
const PreplanEmployeeDashboard = lazy(
  () => import("./features/preplan/pages/PreplanEmployeeDashboard"),
);
const PreplanFormPage = lazy(
  () => import("./features/preplan/pages/PreplanFormPage"),
);
const PaymentRenewalDashboard = lazy(
  () => import("./features/payment-renewal/pages/PaymentRenewalDashboard"),
);
const PaymentRenewalFormPage = lazy(
  () => import("./features/payment-renewal/pages/PaymentRenewalFormPage"),
);
const AssetManagementPage = lazy(
  () => import("./features/assets/AssetManagementPage"),
);
const AssetDetailDashboard = lazy(
  () => import("./features/assets/AssetDetailDashboard"),
);
const ItemMaster = lazy(
  () => import("./features/masters/components/ItemMaster"),
);
const TowerMaintainance = lazy(
  () => import("./features/onm/pages/TowerMaintainance"),
);
const TowerMaintainanceFormPage = lazy(
  () => import("./features/onm/pages/TowerMaintainanceFormPage"),
);
const OnmDashboardPage = lazy(
  () => import("./features/onm/pages/OnmDashboardPage"),
);
const TowerDownTrackerDashboardPage = lazy(
  () => import("./features/onm/pages/TowerDownTrackerDashboardPage"),
);
const TowerDownTrackerPage = lazy(
  () => import("./features/onm/pages/TowerDownTrackerPage"),
);
const TowerDownTrackerFormPage = lazy(
  () => import("./features/onm/pages/TowerDownTrackerFormPage"),
);
const AccessPointMasterPage = lazy(
  () => import("./features/onm/pages/AccessPointMasterPage"),
);
const AccessPointMasterFormPage = lazy(
  () => import("./features/onm/pages/AccessPointMasterFormPage"),
);
const BatteryMasterDataPage = lazy(
  () => import("./features/onm/batteryMaintenance/BatteryMasterDataPage"),
);
const BatteryMasterFormPage = lazy(
  () => import("./features/onm/batteryMaintenance/BatteryMasterFormPage"),
);
const BatteryMaintenancePage = lazy(
  () => import("./features/onm/batteryMaintenance/BatteryMaintenancePage"),
);
const BatteryMaintenanceFormPage = lazy(
  () => import("./features/onm/batteryMaintenance/BatteryMaintenanceFormPage"),
);
const TowerMasterPage = lazy(
  () => import("./features/onm/pages/TowerMasterPage"),
);
const TowerMasterFormPage = lazy(
  () => import("./features/onm/pages/TowerMasterFormPage"),
);
const CreateLcoVendor = lazy(
  () => import("./features/lco_vendor/pages/CreateLcoVendor"),
);
const EditLcoVendor = lazy(
  () => import("./features/lco_vendor/pages/EditLcoVendor"),
);
const ViewLcoVendor = lazy(
  () => import("./features/lco_vendor/pages/ViewLcoVendor"),
);
const LcoVendorList = lazy(
  () => import("./features/lco_vendor/components/LcoVendorList"),
);
const FiberMasterData = lazy(
  () => import("./features/fiber/pages/FiberMasterData"),
);
const FiberMasterFormPage = lazy(
  () => import("./features/fiber/pages/FiberMasterFormPage"),
);
const FiberMaintenance = lazy(
  () => import("./features/fiber/pages/FiberMaintenance"),
);
const FiberMaintenanceFormPage = lazy(
  () => import("./features/fiber/pages/FiberMaintenanceFormPage"),
);
const FiberCutTracker = lazy(
  () => import("./features/fiber/pages/FiberCutTracker"),
);
const OTDRTesting = lazy(() => import("./features/fiber/pages/OTDRTesting"));
const FiberSplicing = lazy(
  () => import("./features/fiber/pages/FiberSplicing"),
);
const FiberLinkMonitoring = lazy(
  () => import("./features/fiber/pages/FiberLinkMonitoring"),
);
const FiberMonthlyReport = lazy(
  () => import("./features/fiber/pages/FiberMonthlyReport"),
);
const FiberMonthlyEntry = lazy(
  () => import("./features/fiber/pages/FiberMonthlyEntry"),
);
const FeasibilityDashboard = lazy(
  () => import("./features/feasibility/pages/FeasibilityDashboard"),
);
const FeasibilityRequestForm = lazy(
  () => import("./features/feasibility/pages/FeasibilityRequestForm"),
);
const FeasibilityRequestDetail = lazy(
  () => import("./features/feasibility/pages/FeasibilityRequestDetail"),
);
/* ==========================================================
   PURCHASE REQUEST / PURCHASE ORDER
========================================================== */

const PurchaseOrderList = lazy(() =>
  import("./features/purchase/pages/PurchaseOrderPage").then((module) => ({
    default: module.PurchaseOrderList,
  })),
);

const PurchaseOrderForm = lazy(() =>
  import("./features/purchase/pages/PurchaseOrderPage").then((module) => ({
    default: module.PurchaseOrderForm,
  })),
);

const PurchaseOrderView = lazy(() =>
  import("./features/purchase/pages/PurchaseOrderPage").then((module) => ({
    default: module.PurchaseOrderView,
  })),
);
const GoodsReceiptNotePage = lazy(() => import("./features/purchase/pages/GoodsReceiptNotePage"));
const GoodsReceiptNoteFormPage = lazy(() => import("./features/purchase/pages/GoodsReceiptNotePage").then((module) => ({ default: module.GoodsReceiptNoteFormPage })));

const isPublicAuthPath = () => {
  if (typeof window === "undefined") return false;
  return ["/login", "/two-factor"].includes(window.location.pathname);
};

function getDefaultRoute() {
  const user = getAuthUser() || {};
  const role = (user.role || getAuthItem("role") || "")
    .toString()
    .trim()
    .toUpperCase();

  if (["ADMIN", "SUPER_ADMIN"].includes(role)) {
    return "/create";
  }

  return resolveModuleAccessRedirectPath(user, "/active");
}

function DefaultRedirect() {
  return <Navigate to={getDefaultRoute()} replace />;
}

export default function App() {
  const [authReady, setAuthReady] = useState(isPublicAuthPath);

  useEffect(() => {
    let isMounted = true;
    let authRefreshInFlight = false;
    let nextAuthRefreshAt = 0;
    let networkRefreshFailureLogged = false;
    clearLegacyAuthStorage();

    if (isPublicAuthPath()) {
      setAuthReady(true);
      return undefined;
    }

    const runCheck = () => {
      if (getAuthItem("token")) {
        checkSession();
      }
    };

    const syncCurrentSessionUser = async () => {
      if (!getAuthItem("token")) {
        return;
      }

      const now = Date.now();
      if (authRefreshInFlight || now < nextAuthRefreshAt) {
        return;
      }

      try {
        authRefreshInFlight = true;
        await refreshCurrentAuthUser({ skipAuthRedirect: true });
        networkRefreshFailureLogged = false;
        nextAuthRefreshAt = 0;
      } catch (error) {
        const isNetworkFailure =
          error?.message === "Network Error" ||
          error?.code === "ERR_NETWORK" ||
          !error?.response;

        if (isNetworkFailure) {
          nextAuthRefreshAt = Date.now() + 30000;
          if (!networkRefreshFailureLogged) {
            console.warn(
              "App auth refresh unavailable; retrying after backend reconnect.",
              error?.message || error,
            );
            networkRefreshFailureLogged = true;
          }
          return;
        }

        console.error("App auth refresh error:", error);
      } finally {
        authRefreshInFlight = false;
      }
    };

    const consumeInventoryEmailAccess = async () => {
      if (typeof window === "undefined") {
        return false;
      }

      const url = new URL(window.location.href);
      const accessToken = String(
        url.searchParams.get("emailAccessToken") || "",
      ).trim();

      if (!accessToken) {
        return false;
      }

      const response = await fetch(
        `${API_BASE_URL}/auth/inventory-email-access`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ accessToken }),
        },
      );
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.message ||
            "Unable to open this inventory request from email.",
        );
      }

      applyAuthLoginPayload(payload);
      await refreshCurrentAuthUser({ skipAuthRedirect: true }).catch(
        (error) => {
          console.error(
            "Refresh auth user after inventory email access error:",
            error,
          );
        },
      );

      url.searchParams.delete("emailAccessToken");
      const nextPath =
        payload?.redirectPath && payload.redirectPath.startsWith("/")
          ? payload.redirectPath
          : `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", nextPath);
      return true;
    };

    const initializeAuth = async () => {
      try {
        await consumeInventoryEmailAccess();
      } catch (error) {
        console.error("Inventory email access error:", error);
      }

      syncSessionTimeouts();
      runCheck();

      if (getAuthItem("token")) {
        await syncCurrentSessionUser();
      }

      if (isMounted) {
        setAuthReady(true);
      }
    };

    initializeAuth();

    const interval = setInterval(() => {
      runCheck();
      syncCurrentSessionUser();
    }, 30000);

    const handleWindowFocus = () => {
      syncCurrentSessionUser();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncCurrentSessionUser();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const unsubscribe = subscribeToAuthSession(() => {
      syncSessionTimeouts();
      runCheck();
    });

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribe();
    };
  }, []);

  if (!authReady) {
    return null;
  }

  return (
    <BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="light"
      />

      <Suspense fallback={null}>
        <Routes>
          {/* PUBLIC */}
          <Route path="/login" element={<Login />} />

          {/* TwoFactor Login Page */}
          <Route path="/two-factor" element={<TwoFactor />} />

          {/* PROTECTED + LAYOUT */}
          <Route element={<Layout />}>
            {/* DEFAULT */}
            <Route path="/" element={<DefaultRedirect />} />

            {/* EMPLOYEE */}
            <Route
              path="/create"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN"]}
                  moduleAccess={{
                    mainMenuKey: "EMPLOYEES",
                    subMenuKey: "CREATE_EMPLOYEE",
                    action: "create",
                  }}
                >
                  <CreateEmployee />
                </ProtectedRoute>
              }
            />

            <Route
              path="/active"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "EMPLOYEES",
                    subMenuKey: "ACTIVE_EMPLOYEES",
                  }}
                >
                  <ActiveEmployees />
                </ProtectedRoute>
              }
            />

            <Route
              path="/relieved"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "EMPLOYEES",
                    subMenuKey: "RELIEVED_EMPLOYEES",
                  }}
                >
                  <RelievedEmployees />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/relieved"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "EMPLOYEES",
                    subMenuKey: "RELIEVED_EMPLOYEES",
                  }}
                >
                  <RelievedEmployees />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/edit/:empId"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "EMPLOYEES",
                    subMenuKey: "ACTIVE_EMPLOYEES",
                  }}
                >
                  <EditEmployee />
                </ProtectedRoute>
              }
            />

            {/* MASTERS */}
            <Route
              path="/masters"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN"]}
                  moduleAccess={{
                    mainMenuKey: "ADMIN_CONSOLE",
                    subMenuKey: "MASTER_ITEMS",
                  }}
                >
                  <ItemMaster />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employees/module-access"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN"]}
                  moduleAccess={{
                    mainMenuKey: "ADMIN_CONSOLE",
                    subMenuKey: "MODULE_ACCESS",
                  }}
                >
                  <ModuleAccessPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employees/module-access/logs"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN"]}
                  moduleAccess={{
                    mainMenuKey: "ADMIN_CONSOLE",
                    subMenuKey: "MODULE_ACCESS_LOGS",
                  }}
                >
                  <ModuleAccessLogsPage />
                </ProtectedRoute>
              }
            />

            {/* TICKETS */}
            <Route
              path="/tickets/create"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "TICKETING",
                    subMenuKey: "NEW_TICKET",
                  }}
                >
                  <NewTicket />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tickets/open"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "TICKETING",
                    subMenuKey: "OPENED_TICKETS",
                  }}
                >
                  <OpenedTickets />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tickets/closed"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "TICKETING",
                    subMenuKey: "CLOSED_TICKETS",
                  }}
                >
                  <ClosedTickets />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tickets/history/:ticketId"
              element={
                <ProtectedRoute moduleAccess={{ mainMenuKey: "TICKETING" }}>
                  <TicketHistoryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ticket-system"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "TICKETING",
                    subMenuKey: "TICKET_SYSTEM_DASHBOARD",
                  }}
                >
                  <TicketSystemDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ticket-system/details"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "TICKETING",
                    subMenuKey: "TICKET_SYSTEM_DETAILS",
                  }}
                >
                  <TicketSystemDetails />
                </ProtectedRoute>
              }
            />

            {/* HRD - OFFER LETTER */}
            <Route
              path="/hrd/offer-letter"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "MANAGEMENT"]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "OFFER_LETTER_REQUEST",
                  }}
                >
                  <OfferLetterDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/offer-letter-request"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "MANAGEMENT"]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "OFFER_LETTER_REQUEST",
                    action: "create",
                  }}
                >
                  <OfferLetterRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/offer-letter/edit/:id"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "MANAGEMENT"]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "OFFER_LETTER_REQUEST",
                    action: "edit",
                  }}
                >
                  <OfferLetterEdit initialMode="edit" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/offer-letter/view/:id"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "MANAGEMENT"]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "OFFER_LETTER_REQUEST",
                  }}
                >
                  <OfferLetterEdit readOnly initialMode="view" />
                </ProtectedRoute>
              }
            />

            {/* âœ… HRD - MANPOWER (FIXED STRUCTURE) */}
            <Route
              path="/hrd/payslip"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "HRD TEAM"]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "PAYSLIP_REQUEST",
                  }}
                >
                  <PayslipDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/payslip/create"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "HRD TEAM"]}
                  designations={[
                    "MANAGER",
                    "HR HEAD",
                    "HEAD HR",
                    "ADMIN",
                    "RECRUITER",
                    "RECRUTIER",
                  ]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "PAYSLIP_REQUEST",
                    action: "create",
                  }}
                >
                  <CreatePayslip />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/payslip/edit/:id"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "HRD TEAM"]}
                  designations={[
                    "MANAGER",
                    "HR HEAD",
                    "HEAD HR",
                    "ADMIN",
                    "RECRUITER",
                    "RECRUTIER",
                  ]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "PAYSLIP_REQUEST",
                    action: "edit",
                  }}
                >
                  <EditPayslip />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/signatures"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD", "HRD TEAM", "MANAGEMENT"]}
                  designations={[
                    "MANAGER",
                    "HR HEAD",
                    "HEAD HR",
                    "ADMIN",
                    "RECRUITER",
                    "RECRUTIER",
                    "MD",
                    "CEO",
                  ]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "SIGNATURE_CENTER",
                  }}
                >
                  <SignatureCenter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/relieving"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD"]}
                  designations={[
                    "MANAGER",
                    "HR HEAD",
                    "HEAD HR",
                    "RECRUITER",
                    "RECRUTIER",
                    "ADMIN",
                  ]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "RELIEVING_REQUEST",
                  }}
                >
                  <RelievedDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/relieving/create"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD"]}
                  designations={[
                    "MANAGER",
                    "HR HEAD",
                    "HEAD HR",
                    "RECRUITER",
                    "RECRUTIER",
                    "ADMIN",
                  ]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "RELIEVING_REQUEST",
                    action: "create",
                  }}
                >
                  <RelievingRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/relieving/create/:employeeId"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD"]}
                  designations={[
                    "MANAGER",
                    "HR HEAD",
                    "HEAD HR",
                    "RECRUITER",
                    "RECRUTIER",
                    "ADMIN",
                  ]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "RELIEVING_REQUEST",
                    action: "create",
                  }}
                >
                  <RelievingRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/relieving/edit/:id"
              element={
                <ProtectedRoute
                  roles={["ADMIN", "SUPER_ADMIN", "USER_ACCOUNT"]}
                  teams={["HRD"]}
                  designations={[
                    "MANAGER",
                    "HR HEAD",
                    "HEAD HR",
                    "RECRUITER",
                    "RECRUTIER",
                    "ADMIN",
                  ]}
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "RELIEVING_REQUEST",
                    action: "edit",
                  }}
                >
                  <EditRelieving />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/manpower"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "MANPOWER_REQUEST",
                  }}
                >
                  <ManpowerRequest />
                </ProtectedRoute>
              }
            />

            {/* âœ… NEW REQUEST PAGE */}
            <Route
              path="/hrd/manpower/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "MANPOWER_REQUEST",
                    action: "create",
                  }}
                >
                  <NewRequest />
                </ProtectedRoute>
              }
            />

            {/* âœ… REQUEST DETAIL PAGE */}
            <Route
              path="/hrd/manpower/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "MANPOWER_REQUEST",
                  }}
                >
                  <RequestDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "MRS_REQUEST",
                  }}
                >
                  <InventoryDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/purchase/requests"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "PURCHASE",
                    subMenuKey: "PURCHASE_REQUEST",
                  }}
                >
                  <InventoryDashboard purchase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/orders"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "PURCHASE",
                    subMenuKey: "PURCHASE_ORDER",
                  }}
                >
                  <PurchaseOrderList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/grn"
              element={
                <ProtectedRoute
                  moduleAccess={{ mainMenuKey: "PURCHASE", subMenuKey: "GRN" }}
                >
                  <GoodsReceiptNotePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/grn/new"
              element={
                <ProtectedRoute
                  moduleAccess={{ mainMenuKey: "PURCHASE", subMenuKey: "GRN", action: "create" }}
                >
                  <GoodsReceiptNoteFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/grn/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{ mainMenuKey: "PURCHASE", subMenuKey: "GRN" }}
                >
                  <GoodsReceiptNoteFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/orders/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "PURCHASE",
                    subMenuKey: "PURCHASE_ORDER",
                    action: "create",
                  }}
                >
                  <PurchaseOrderForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/orders/:id/edit"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "PURCHASE",
                    subMenuKey: "PURCHASE_ORDER",
                    action: "edit",
                  }}
                >
                  <PurchaseOrderForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/orders/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "PURCHASE",
                    subMenuKey: "PURCHASE_ORDER",
                  }}
                >
                  <PurchaseOrderView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/requests/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "PURCHASE",
                    subMenuKey: "PURCHASE_REQUEST",
                    action: "create",
                  }}
                >
                  <InventoryNewRequest purchase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/requests/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "PURCHASE",
                    subMenuKey: "PURCHASE_REQUEST",
                  }}
                >
                  <InventoryRequestDetail purchase />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "MRS_REQUEST",
                    action: "create",
                  }}
                >
                  <InventoryNewRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory/requests/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "MRS_REQUEST",
                  }}
                >
                  <InventoryRequestDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory/stock-maintainance"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "STOCK_MAINTAINANCE",
                  }}
                >
                  <StockMaintainancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory/inventory-master"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "INVENTORY_MASTER",
                  }}
                >
                  <InventoryMasterPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory/vendor-register"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "VENDOR_REGISTER",
                  }}
                >
                  <VendorRegisterDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory/vendor-register/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "VENDOR_REGISTER",
                    action: "create",
                  }}
                >
                  <VendorRegisterFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory/vendor-register/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "INVENTORY",
                    subMenuKey: "VENDOR_REGISTER",
                    action: "edit",
                  }}
                >
                  <VendorRegisterFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/inventory"
              element={<Navigate to="/inventory" replace />}
            />

            <Route
              path="/hrd/inventory/new"
              element={<Navigate to="/inventory/new" replace />}
            />

            <Route
              path="/hrd/inventory/stock-maintainance"
              element={<Navigate to="/inventory/stock-maintainance" replace />}
            />

            <Route
              path="/hrd/inventory/inventory-master"
              element={<Navigate to="/inventory/inventory-master" replace />}
            />

            <Route
              path="/hrd/inventory/vendor-register"
              element={<Navigate to="/inventory/vendor-register" replace />}
            />

            {/* UNIFORM */}
            <Route
              path="/hrd/uniform"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "SERVICE_REQUESTS",
                  }}
                >
                  <UniformRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hrd/uniform/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "HRD",
                    subMenuKey: "SERVICE_REQUESTS",
                    action: "create",
                  }}
                >
                  <UniformRequestForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-onboarding/lead-process"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "LEAD",
                    subMenuKey: "NEW_CONNECTION_REQUESTS",
                  }}
                >
                  <LeadMasterPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-onboarding/lead-process/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "LEAD",
                    subMenuKey: "NEW_CONNECTION_REQUESTS",
                    action: "create",
                  }}
                >
                  <LeadFormPage mode="create" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-onboarding/lead-process/view/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "LEAD",
                    subMenuKey: "NEW_CONNECTION_REQUESTS",
                  }}
                >
                  <LeadFormPage mode="view" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-onboarding/lead-process/edit/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "LEAD",
                    subMenuKey: "NEW_CONNECTION_REQUESTS",
                    action: "edit",
                  }}
                >
                  <LeadFormPage mode="edit" />
                </ProtectedRoute>
              }
            />

            {/* =========================================================
    CUSTOMER ONBOARDING - FEASIBILITY
       ========================================================= */}

            <Route
              path="/customer-onboarding/feasibility"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "LEAD",
                    subMenuKey: "FEASIBILITY",
                  }}
                >
                  <FeasibilityDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-onboarding/feasibility/new"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "LEAD",
                    subMenuKey: "FEASIBILITY",
                    action: "create",
                  }}
                >
                  <FeasibilityRequestForm mode="create" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-onboarding/feasibility/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "LEAD",
                    subMenuKey: "FEASIBILITY",
                  }}
                >
                  <FeasibilityRequestDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/lco"
              element={
                <ProtectedRoute
                  moduleAccess={{ mainMenuKey: "CRM", subMenuKey: "LCO" }}
                >
                  <LcoDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/lco/create"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "CRM",
                    subMenuKey: "LCO",
                    action: "create",
                  }}
                >
                  <CreateLco />
                </ProtectedRoute>
              }
            />

            <Route
              path="/lco/:id"
              element={
                <ProtectedRoute
                  moduleAccess={{ mainMenuKey: "CRM", subMenuKey: "LCO" }}
                >
                  <ViewLco />
                </ProtectedRoute>
              }
            />

            <Route
              path="/lco/:id/edit"
              element={
                <ProtectedRoute
                  moduleAccess={{
                    mainMenuKey: "CRM",
                    subMenuKey: "LCO",
                    action: "edit",
                  }}
                >
                  <EditLco />
                </ProtectedRoute>
              }
            />

            {/* =========================================================
              CRM - LCO VENDOR MANAGEMENT
          ========================================================= */}


          <Route
            path="/lco-vendors"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "VENDOR_LCO",
                  action: "view",
                }}
              >
                <LcoVendorList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/lco-vendors/create"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "VENDOR_LCO",
                  action: "create",
                }}
              >
                <CreateLcoVendor />
              </ProtectedRoute>
            }
          />

          <Route
            path="/lco-vendors/:id"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "VENDOR_LCO",
                  action: "view",
                }}
              >
                <ViewLcoVendor />
              </ProtectedRoute>
            }
          />

          <Route
            path="/lco-vendors/:id/edit"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "VENDOR_LCO",
                  action: "edit",
                }}
              >
                <EditLcoVendor />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer-details"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DETAILS",
                }}
              >
                <CustomerDetailsDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer-details/create"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DETAILS",
                  action: "create",
                }}
              >
                <CreateCustomerDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer-details/:id"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DETAILS",
                }}
              >
                <ViewCustomerDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer-details/:id/edit"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DETAILS",
                  action: "edit",
                }}
              >
                <EditCustomerDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm/customer-delight"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DELIGHT",
                }}
              >
                <CustomerDelightDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm/customer-delight/analytics"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DELIGHT_ANALYTICS",
                }}
              >
                <CustomerDelightAnalyticsDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm/customer-delight/create"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DELIGHT",
                  action: "create",
                }}
              >
                <CreateCustomerDelight />
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm/customer-delight/:id"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DELIGHT",
                }}
              >
                <ViewCustomerDelight />
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm/customer-delight/:id/edit"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "CUSTOMER_DELIGHT",
                  action: "edit",
                }}
              >
                <EditCustomerDelight />
              </ProtectedRoute>
            }
          />

          <Route
            path="/chat"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CHAT",
                  subMenuKey: "OFFICIAL_CHAT",
                }}
              >
                <ChatPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/chat/meetings"
            element={
              <ProtectedRoute
                moduleAccess={{ mainMenuKey: "CHAT", subMenuKey: "MEETING" }}
              >
                <MeetingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/support/it-contact-matrix"
            element={
              <ProtectedRoute>
                <ITContactMatrix />
              </ProtectedRoute>
            }
          />

          <Route
            path="/assets"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "IT",
                  subMenuKey: "ASSET_MANAGEMENT",
                }}
              >
                <AssetManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/assets/details/:assetId"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "IT",
                  subMenuKey: "ASSET_MANAGEMENT",
                }}
              >
                <AssetDetailDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/assets/:assetType"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "IT",
                  subMenuKey: "ASSET_MANAGEMENT",
                }}
              >
                <AssetManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer/nld-tracker"
            element={
              <ProtectedRoute
                moduleAccess={{ mainMenuKey: "NOC", subMenuKey: "NLD_TRACKER" }}
              >
                <NocDashboardPage />
              </ProtectedRoute>
            }
          />


          <Route
  path="/noc/tower-status"
  element={
    <ProtectedRoute
      moduleAccess={{
        mainMenuKey: "NOC",
        subMenuKey: "TOWER_STATUS",
      }}
    >
      <TowerStatus />
    </ProtectedRoute>
  }
/>

          <Route
            path="/customer/nld-tracker/new"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "NOC",
                  subMenuKey: "NLD_TRACKER",
                  action: "create",
                }}
              >
                <NocFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer/nld-tracker/view/:id"
            element={
              <ProtectedRoute
                moduleAccess={{ mainMenuKey: "NOC", subMenuKey: "NLD_TRACKER" }}
              >
                <NocDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer/nld-tracker/edit/:id"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "NOC",
                  subMenuKey: "NLD_TRACKER",
                  action: "edit",
                }}
              >
                <NocFormPage mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/noc/nld-master"
            element={
              <ProtectedRoute
                moduleAccess={{ mainMenuKey: "NOC", subMenuKey: "NLD_MASTER" }}
              >
                <NldMasterPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/noc/nld-master/new"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "NOC",
                  subMenuKey: "NLD_MASTER",
                  action: "create",
                }}
              >
                <NldMasterAdd />
              </ProtectedRoute>
            }
          />

          <Route
            path="/noc/nld-master/details/:id"
            element={
              <ProtectedRoute
                moduleAccess={{ mainMenuKey: "NOC", subMenuKey: "NLD_MASTER" }}
              >
                <NldMasterDetail mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/noc/nld-master/edit/:id"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "NOC",
                  subMenuKey: "NLD_MASTER",
                  action: "edit",
                }}
              >
                <NldMasterDetail mode="edit" />
              </ProtectedRoute>
            }
          />

          {/* PREPLAN */}
          <Route
            path="/preplan/dashboard"
            element={
              <ProtectedRoute moduleAccess={{ mainMenuKey: "CRM", subMenuKey: "PREPLAN_DASHBOARD" }}>
                <PreplanEmployeeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preplan"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "PREPLAN_DASHBOARD",
                }}
              >
                <PreplanDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/preplan/new"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "PREPLAN_DASHBOARD",
                  action: "create",
                }}
              >
                <PreplanFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/preplan/:id"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "CRM",
                  subMenuKey: "PREPLAN_DASHBOARD",
                }}
              >
                <PreplanFormPage />
              </ProtectedRoute>
            }
          />

          {/* PAYMENT REMINDER */}
          <Route
            path="/payment-renewal"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "IT",
                  subMenuKey: "PAYMENT_RENEWAL_DASHBOARD",
                }}
              >
                <PaymentRenewalDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-renewal/new"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "IT",
                  subMenuKey: "PAYMENT_RENEWAL_DASHBOARD",
                  action: "create",
                }}
              >
                <PaymentRenewalFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-renewal/:id"
            element={
              <ProtectedRoute
                moduleAccess={{
                  mainMenuKey: "IT",
                  subMenuKey: "PAYMENT_RENEWAL_DASHBOARD",
                }}
              >
                <PaymentRenewalFormPage />
              </ProtectedRoute>
            }
          />

          {/* ONM */}
          <Route
            path="/onm/dashboard"
            element={
              <ProtectedRoute onmAccess={{ subMenuKey: "DASHBOARD" }}>
                <OnmDashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-maintenance"
            element={
              <ProtectedRoute onmAccess={{ subMenuKey: "TOWER_MAINTENANCE" }}>
                <TowerMaintainance />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-maintenance/new"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "TOWER_MAINTENANCE",
                  action: "create",
                }}
              >
                <TowerMaintainanceFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-maintenance/edit/:id"
            element={
              <ProtectedRoute
                onmAccess={{ subMenuKey: "TOWER_MAINTENANCE", action: "edit" }}
              >
                <TowerMaintainanceFormPage mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-maintenance/view/:id"
            element={
              <ProtectedRoute
                onmAccess={{ subMenuKey: "TOWER_MAINTENANCE", action: "view" }}
              >
                <TowerMaintainanceFormPage mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-master-data"
            element={
              <ProtectedRoute onmAccess={{ subMenuKey: "TOWER_MASTER_DATA" }}>
                <TowerMasterPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-master-data/new"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "TOWER_MASTER_DATA",
                  action: "create",
                }}
              >
                <TowerMasterFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-master-data/edit/:id"
            element={
              <ProtectedRoute
                onmAccess={{ subMenuKey: "TOWER_MASTER_DATA", action: "edit" }}
              >
                <TowerMasterFormPage mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-master-data/view/:id"
            element={
              <ProtectedRoute
                onmAccess={{ subMenuKey: "TOWER_MASTER_DATA", action: "view" }}
              >
                <TowerMasterFormPage mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-down-tracker/dashboard"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "TOWER_DOWN_TRACKER_DASHBOARD",
                  action: "view",
                }}
              >
                <TowerDownTrackerDashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-down-tracker"
            element={
              <ProtectedRoute onmAccess={{ subMenuKey: "TOWER_DOWN_TRACKER" }}>
                <TowerDownTrackerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-down-tracker/new"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "TOWER_DOWN_TRACKER",
                  action: "create",
                }}
              >
                <TowerDownTrackerFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-down-tracker/edit/:id"
            element={
              <ProtectedRoute
                onmAccess={{ subMenuKey: "TOWER_DOWN_TRACKER", action: "edit" }}
              >
                <TowerDownTrackerFormPage mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/tower-down-tracker/view/:id"
            element={
              <ProtectedRoute
                onmAccess={{ subMenuKey: "TOWER_DOWN_TRACKER", action: "view" }}
              >
                <TowerDownTrackerFormPage mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/access-point-master"
            element={
              <ProtectedRoute onmAccess={{ subMenuKey: "ACCESS_POINT_MASTER" }}>
                <AccessPointMasterPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/access-point-master/new"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "ACCESS_POINT_MASTER",
                  action: "create",
                }}
              >
                <AccessPointMasterFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/access-point-master/edit/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "ACCESS_POINT_MASTER",
                  action: "edit",
                }}
              >
                <AccessPointMasterFormPage mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/access-point-master/view/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "ACCESS_POINT_MASTER",
                  action: "view",
                }}
              >
                <AccessPointMasterFormPage mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-master-data"
            element={
              <ProtectedRoute onmAccess={{ subMenuKey: "BATTERY_MASTER_DATA" }}>
                <BatteryMasterDataPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-master-data/new"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "BATTERY_MASTER_DATA",
                  action: "create",
                }}
              >
                <BatteryMasterFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-master-data/edit/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "BATTERY_MASTER_DATA",
                  action: "edit",
                }}
              >
                <BatteryMasterFormPage mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-master-data/view/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "BATTERY_MASTER_DATA",
                  action: "view",
                }}
              >
                <BatteryMasterFormPage mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-maintenance"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "BATTERY_MAINTENANCE",
                  action: "view",
                }}
              >
                <BatteryMaintenancePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-maintenance/new"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "BATTERY_MAINTENANCE",
                  action: "create",
                }}
              >
                <BatteryMaintenanceFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-maintenance/edit/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "BATTERY_MAINTENANCE",
                  action: "edit",
                }}
              >
                <BatteryMaintenanceFormPage mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onm/battery-maintenance/view/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "BATTERY_MAINTENANCE",
                  action: "view",
                }}
              >
                <BatteryMaintenanceFormPage mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/master-data"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_MASTER_DATA",
                  action: "view",
                }}
              >
                <FiberMasterData />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/master-data/new"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_MASTER_DATA",
                  action: "create",
                }}
              >
                <FiberMasterFormPage mode="create" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/master-data/edit/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_MASTER_DATA",
                  action: "edit",
                }}
              >
                <FiberMasterFormPage mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/master-data/view/:id"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_MASTER_DATA",
                }}
              >
                <FiberMasterFormPage mode="view" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/maintenance"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_MAINTENANCE",
                }}
              >
                <FiberMaintenance />
              </ProtectedRoute>
            }
          />

          <Route path="/fiber/maintenance/new" element={<ProtectedRoute onmAccess={{ subMenuKey: "FIBER_MAINTENANCE", action: "create" }}><FiberMaintenanceFormPage mode="create" /></ProtectedRoute>} />
          <Route path="/fiber/maintenance/edit/:id" element={<ProtectedRoute onmAccess={{ subMenuKey: "FIBER_MAINTENANCE", action: "edit" }}><FiberMaintenanceFormPage mode="edit" /></ProtectedRoute>} />

          <Route
            path="/fiber/monthly-report"
            element={
              <ProtectedRoute
                moduleAccess={{
                  anyOf: [
                    {
                      mainMenuKey: "FIBER_MANAGEMENT",
                      subMenuKey: "FIBER_MONTHLY_REPORT",
                    },
                    {
                      mainMenuKey: "ONM",
                      subMenuKey: "FIBER_MONTHLY_REPORT",
                    },
                  ],
                }}
              >
                <FiberMonthlyReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/monthly-entry"
            element={
              <ProtectedRoute
                moduleAccess={{
                  anyOf: [
                    {
                      mainMenuKey: "FIBER_MANAGEMENT",
                      subMenuKey: "FIBER_MONTHLY_REPORT",
                      action: "create",
                    },
                    {
                      mainMenuKey: "ONM",
                      subMenuKey: "FIBER_MONTHLY_REPORT",
                      action: "create",
                    },
                  ],
                }}
              >
                <FiberMonthlyEntry />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/cut-tracker"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_CUT_TRACKER",
                }}
              >
                <FiberCutTracker />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/otdr-testing"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "OTDR_TESTING",
                }}
              >
                <OTDRTesting />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/splicing"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_SPLICING",
                }}
              >
                <FiberSplicing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fiber/monitoring"
            element={
              <ProtectedRoute
                onmAccess={{
                  subMenuKey: "FIBER_MONITORING",
                }}
              >
                <FiberLinkMonitoring />
              </ProtectedRoute>
            }
          />
          {/* 404 */}
          <Route path="*" element={<PageNotFound />} />
        </Route>

        {/* FALLBACK */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>

    </BrowserRouter>
  );
}
